import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSnippet } from '../../contexts/SnippetContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Snippet {
  id: string;
  snippetId: string;
  websiteUrl: string;
  isVerified: boolean;
  status: string;
  settings: {
    seo: boolean;
    geo: boolean;
    pageSpeed: boolean;
    captureDelay: number;
    maxRetries: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface SnippetDetails {
  snippet: Snippet & {
    snippetCode: string;
  };
}

const Snippets: React.FC = () => {
  const { user } = useAuth();
  const { fetchSnippets: contextFetchSnippets } = useSnippet();
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [selectedSnippet, setSelectedSnippet] = useState<SnippetDetails | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSnippetCode, setShowSnippetCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    websiteUrl: '',
    settings: {
      seo: true,
      geo: true,
      pageSpeed: true,
      captureDelay: 2000,
      maxRetries: 3
    }
  });

  useEffect(() => {
    loadSnippets();
  }, []);

  const loadSnippets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/snippets');
      setSnippets(response.data.snippets);
    } catch (error) {
      console.error('Error fetching snippets:', error);
      toast.error('Failed to load snippets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSnippet = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/snippets', formData);
      toast.success('Website added successfully!');
      setShowCreateForm(false);
      setFormData({
        websiteUrl: '',
        settings: {
          seo: true,
          geo: true,
          pageSpeed: true,
          captureDelay: 2000,
          maxRetries: 3
        }
      });
      loadSnippets();
      contextFetchSnippets(); // Refresh the global snippet context
    } catch (error: any) {
      console.error('Error creating snippet:', error);
      toast.error(error.response?.data?.message || 'Failed to add website');
    }
  };

  const handleViewSnippet = async (snippetId: string) => {
    try {
      const response = await api.get(`/snippets/${snippetId}`);
      setSelectedSnippet(response.data);
      setShowSnippetCode(true);
    } catch (error) {
      console.error('Error fetching snippet details:', error);
      toast.error('Failed to load snippet details');
    }
  };

  const handleVerifySnippet = async (snippetId: string) => {
    try {
      setVerifying(snippetId);
      const response = await api.post(`/snippets/${snippetId}/verify`);
      
      if (response.data.isVerified) {
        toast.success('Snippet verified successfully!');
        // Refresh both local and global snippet data
        await contextFetchSnippets(); // Refresh the global snippet context
      } else {
        toast.error('Snippet not detected. Please make sure it\'s properly installed on your website.');
      }
    } catch (error) {
      console.error('Error verifying snippet:', error);
      toast.error('Failed to verify snippet');
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteSnippet = async (snippetId: string) => {
    if (!window.confirm('Are you sure you want to delete this website? This will also delete all captured pages.')) {
      return;
    }

    try {
      await api.delete(`/snippets/${snippetId}`);
      toast.success('Website deleted successfully!');
      loadSnippets();
      contextFetchSnippets(); // Refresh the global snippet context
      if (selectedSnippet?.snippet.id === snippetId) {
        setSelectedSnippet(null);
        setShowSnippetCode(false);
      }
    } catch (error) {
      console.error('Error deleting snippet:', error);
      toast.error('Failed to delete website');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Website code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Website Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          <i className="fas fa-plus me-2"></i>
          Add New Website
        </button>
      </div>

      {/* Create Snippet Modal */}
      {showCreateForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Website</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateForm(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateSnippet}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Website URL *</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      placeholder="https://example.com"
                      required
                    />
                  </div>

                  <h6 className="mb-3">Optimization Settings</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.settings.seo}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, seo: e.target.checked }
                          })}
                        />
                        <label className="form-check-label">SEO Optimization</label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.settings.geo}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, geo: e.target.checked }
                          })}
                        />
                        <label className="form-check-label">GEO Optimization</label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.settings.pageSpeed}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, pageSpeed: e.target.checked }
                          })}
                        />
                        <label className="form-check-label">Page Speed Optimization</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Capture Delay (ms)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.settings.captureDelay}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, captureDelay: parseInt(e.target.value) }
                          })}
                          min="0"
                          max="10000"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Max Retries</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.settings.maxRetries}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, maxRetries: parseInt(e.target.value) }
                          })}
                          min="1"
                          max="10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Website
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Snippet Code Modal */}
      {showSnippetCode && selectedSnippet && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Website Code for {selectedSnippet.snippet.websiteUrl}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSnippetCode(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  Copy this code and paste it into your website's HTML, just before the closing <code>&lt;/body&gt;</code> tag.
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Snippet Code:</label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => copyToClipboard(selectedSnippet.snippet.snippetCode)}
                    >
                      <i className="fas fa-copy me-1"></i>
                      Copy
                    </button>
                  </div>
                  <pre className="bg-light p-3 rounded" style={{ fontSize: '0.875rem', maxHeight: '400px', overflow: 'auto' }}>
                    <code>{selectedSnippet.snippet.snippetCode}</code>
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSnippetCode(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snippets List */}
      {snippets.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-code fa-3x text-muted mb-3"></i>
          <h4 className="text-muted">No Websites Added</h4>
          <p className="text-muted">Add your first website to start capturing and optimizing pages.</p>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Add Your First Website
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Website URL</th>
                <th>Snippet ID</th>
                <th>Status</th>
                <th>Settings</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {snippets.map((snippet) => (
                <tr key={snippet.id}>
                  <td>
                    <a href={snippet.websiteUrl} target="_blank" rel="noopener noreferrer">
                      {snippet.websiteUrl}
                    </a>
                  </td>
                  <td>
                    <code className="text-sm">{snippet.snippetId}</code>
                  </td>
                  <td>
                    {snippet.isVerified ? (
                      <span className="badge bg-success">
                        <i className="fas fa-check me-1"></i>
                        Verified
                      </span>
                    ) : (
                      <span className="badge bg-warning">
                        <i className="fas fa-clock me-1"></i>
                        Pending
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {snippet.settings.seo && (
                        <span className="badge bg-info">SEO</span>
                      )}
                      {snippet.settings.geo && (
                        <span className="badge bg-primary">GEO</span>
                      )}
                      {snippet.settings.pageSpeed && (
                        <span className="badge bg-secondary">Speed</span>
                      )}
                    </div>
                  </td>
                  <td>{new Date(snippet.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => handleViewSnippet(snippet.id)}
                        title="View Code"
                      >
                        <i className="fas fa-code"></i>
                      </button>
                      {!snippet.isVerified && (
                        <button
                          className="btn btn-outline-success"
                          onClick={() => handleVerifySnippet(snippet.id)}
                          disabled={verifying === snippet.id}
                          title="Verify Installation"
                        >
                          {verifying === snippet.id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="fas fa-check"></i>
                          )}
                        </button>
                      )}
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDeleteSnippet(snippet.id)}
                        title="Delete Website"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Snippets;
